import React, { useState, useEffect } from 'react';
import { getDatabase, ref as dbRef, get, set, update } from "firebase/database";
import { getAuth } from 'firebase/auth';
import './GlobalSettingsModal.css';
import SEOSettings from './SEOSettings';
import StandardSettings from './components/StandardSettings';
import FontsSettings from './components/FontsSettings';
import AccessControl from './AccessControl';
import { useUserProducts } from './hooks/useProductData';
import FontSyncModal from './components/FontSyncModal';
import { migrateToContainerSections } from './WidgetTypes/HeroHeaderContainerHelpers';
import { replacePageFonts, replaceHeroHeaderFonts } from '../utils/fontReplacer';

const GlobalSettingsModal = ({ show, onClose, pageId }) => {
    const defaultBigFont = "Voyage"; // Setzen Sie hier Ihre Standard-Schriftart fest
    const defaultSmallFont = "Jost";
    
    // Parse pageId to detect if it's a space-based page
    const parsePageId = (pageId) => {
        if (!pageId) return { isSpacePage: false };
        
        const parts = pageId.split('/');
        if (parts.length === 3) {
            // 3-level structure: userId/spaceId/pageName
            return {
                isSpacePage: true,
                userId: parts[0],
                spaceId: parts[1],
                pageName: parts[2]
            };
        } else if (parts.length === 2) {
            // 2-level structure: userId/pageName (legacy)
            return {
                isSpacePage: false,
                userId: parts[0],
                pageName: parts[1]
            };
        }
        return { isSpacePage: false };
    };
    
    const pageInfo = parsePageId(pageId); 

    const [pageSettings, setPageSettings] = useState({
        name: '',
        description: '',
        customDomain: '',
        isActive: false,
        globalBigFont: defaultBigFont,
        globalSmallFont: defaultSmallFont,
        whatsappStatus: false, 
        whatsappNumber: '', 
        translate1: false,
        translate2: false,
        colors: {
            color1: '#FFFFFF',
            color2: '#000000',
            color3: '#FF0000',
            color4: '#00FF00',
        },
        backgroundColor: '#FFFFFF',
        livePageImg: '',
        navBarSettings: {
            enabled: false,
            // Default settings preserved even when disabled
            position: 'top',
            backgroundStyle: 'solid',
            backgroundColor: '#ffffff',
            glassmorphism: false,
            floatingGlass: false,
            textColor: '#1f2937',
            buttonBackgroundColor: '#667eea',
            buttonTextColor: '#ffffff',
            height: 70,
            width: 100,
            borderRadius: 0,
            opacity: 100,
            backdropBlur: 0,
            showLogo: true,
            layoutType: 'traditional',
            logoMaxWidth: 150,
            logoMaxWidthMobile: 120,
            socialIconBackground: 'rgba(102, 126, 234, 0.1)',
            socialIconInvert: false,
            socialIconBorderRadius: 10,
            socialIconSize: 40,
            buttonBorderRadius: 8,
            buttonWidth: 'auto',
            mobileCollapse: true,
            smoothScroll: true,
            hideOnScroll: false,
            navItems: [],
            socialLinks: [],
            ctaButton: null
        }
    });
    const db = getDatabase();
    const auth = getAuth();
    const currentUser = auth.currentUser;
    const [previousCustomDomain, setPreviousCustomDomain] = useState('');
    const [selectedFont, setSelectedFont] = useState('');
    const [selectedSmallFont, setSelectedSmallFont] = useState('');
    const [activeTab, setActiveTab] = useState('standard'); // Tab state
    const [isClosing, setIsClosing] = useState(false); // Animation state
    const [previewDevice, setPreviewDevice] = useState('desktop'); // 'desktop' or 'mobile'
    
    // Font Sync Modal State
    const [showFontSyncModal, setShowFontSyncModal] = useState(false);
    const [pendingSaveData, setPendingSaveData] = useState(null);
    const [oldFonts, setOldFonts] = useState(null);
    
    // Get effective navbar settings for preview
    const getEffectiveNavBarSettings = () => {
        if (!pageSettings.navBarSettings) return {};
        
        const deviceKey = previewDevice === 'desktop' ? 'desktopSettings' : 'mobileSettings';
        const deviceSettings = pageSettings.navBarSettings[deviceKey] || {};
        return { ...pageSettings.navBarSettings, ...deviceSettings };
    };

    // Render navbar content based on device and layout
    const renderGlobalNavBarContent = () => {
        const effectiveSettings = getEffectiveNavBarSettings();
        
        // MOBILE LAYOUTS - Reduzierte Darstellung
        if (previewDevice === 'mobile') {
            const currentMobileLayout = effectiveSettings.mobileLayout || effectiveSettings.layoutType;
            
            console.log('🔍 GlobalNavBar Mobile Layout:', {
                previewDevice,
                mobileLayout: effectiveSettings.mobileLayout,
                layoutType: effectiveSettings.layoutType,
                currentMobileLayout,
                showLogo: effectiveSettings.showLogo,
                logoMaxWidthMobile: effectiveSettings.logoMaxWidthMobile,
                mobileSettings: pageSettings.navBarSettings?.mobileSettings
            });
            
            // MOBILE LAYOUTS
            switch (currentMobileLayout) {
                case 'logo-center':
                case 'mobile-logo-center':
                    // Logo zentriert
                    return (
                        <div className="global-navbar-preview__mobile-center">
                            <div className="global-navbar-preview__logo">
                                {pageSettings.livePageImg ? (
                                    <img 
                                        src={pageSettings.livePageImg} 
                                        alt="Logo"
                                        className="global-navbar-preview__logo-img"
                                        style={{
                                            maxWidth: `${effectiveSettings.logoMaxWidthMobile || 120}px`,
                                            width: `${effectiveSettings.logoMaxWidthMobile || 120}px`,
                                            height: 'auto'
                                        }}
                                    />
                                ) : (
                                    <span 
                                        className="global-navbar-preview__logo-text"
                                        style={{
                                            maxWidth: `${effectiveSettings.logoMaxWidthMobile || 120}px`,
                                            width: `${effectiveSettings.logoMaxWidthMobile || 120}px`
                                        }}
                                    >
                                        {pageSettings.name || 'Your Brand'}
                                    </span>
                                )}
                            </div>
                        </div>
                    );

                case 'logo-button':
                case 'mobile-logo-button':
                    // Logo links, Button rechts
                    return (
                        <>
                            <div className="global-navbar-preview__logo">
                                {pageSettings.livePageImg ? (
                                    <img 
                                        src={pageSettings.livePageImg} 
                                        alt="Logo"
                                        className="global-navbar-preview__logo-img"
                                        style={{
                                            maxWidth: `${effectiveSettings.logoMaxWidthMobile || 120}px`,
                                            width: `${effectiveSettings.logoMaxWidthMobile || 120}px`,
                                            height: 'auto'
                                        }}
                                    />
                                ) : (
                                    <span 
                                        className="global-navbar-preview__logo-text"
                                        style={{
                                            maxWidth: `${effectiveSettings.logoMaxWidthMobile || 120}px`,
                                            width: `${effectiveSettings.logoMaxWidthMobile || 120}px`
                                        }}
                                    >
                                        {pageSettings.name || 'Your Brand'}
                                    </span>
                                )}
                            </div>
                            <div 
                                className="global-navbar-preview__mobile-button"
                                style={{
                                    background: effectiveSettings.mobileButton?.backgroundColor || '#667eea',
                                    color: effectiveSettings.mobileButton?.textColor || '#ffffff',
                                    fontSize: `${effectiveSettings.mobileButton?.fontSize || 14}px`,
                                    borderRadius: `${effectiveSettings.mobileButton?.borderRadius ?? 8}px`,
                                    padding: '8px 16px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    border: 'none'
                                }}
                            >
                                {effectiveSettings.mobileButton?.text || 'Contact'}
                            </div>
                        </>
                    );

                case 'logo-social':
                case 'mobile-logo-social':
                    // Logo links, Social Icon rechts
                    return (
                        <>
                            <div className="global-navbar-preview__logo">
                                {pageSettings.livePageImg ? (
                                    <img 
                                        src={pageSettings.livePageImg} 
                                        alt="Logo"
                                        className="global-navbar-preview__logo-img"
                                        style={{
                                            maxWidth: `${effectiveSettings.logoMaxWidthMobile || 120}px`,
                                            width: `${effectiveSettings.logoMaxWidthMobile || 120}px`,
                                            height: 'auto'
                                        }}
                                    />
                                ) : (
                                    <span 
                                        className="global-navbar-preview__logo-text"
                                        style={{
                                            maxWidth: `${effectiveSettings.logoMaxWidthMobile || 120}px`,
                                            width: `${effectiveSettings.logoMaxWidthMobile || 120}px`
                                        }}
                                    >
                                        {pageSettings.name || 'Your Brand'}
                                    </span>
                                )}
                            </div>
                            <div 
                                className="global-navbar-preview__mobile-social"
                                style={{
                                    background: effectiveSettings.mobileSocial?.backgroundColor || '#667eea',
                                    borderRadius: `${effectiveSettings.mobileSocial?.borderRadius ?? 10}px`,
                                    width: `${effectiveSettings.mobileSocial?.containerSize ?? 40}px`,
                                    height: `${effectiveSettings.mobileSocial?.containerSize ?? 40}px`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer'
                                }}
                            >
                                <img 
                                    src={`/img/${effectiveSettings.mobileSocial?.platform || 'instagram'}.svg`} 
                                    alt={effectiveSettings.mobileSocial?.platform || 'instagram'}
                                    style={{ 
                                        width: `${effectiveSettings.mobileSocial?.iconSize ?? 20}px`, 
                                        height: `${effectiveSettings.mobileSocial?.iconSize ?? 20}px`, 
                                        filter: effectiveSettings.mobileSocial?.invertIcon ?? true 
                                            ? 'brightness(0) invert(1)' 
                                            : 'brightness(0)'
                                    }}
                                />
                            </div>
                        </>
                    );

                default:
                    // Fallback - Logo zentriert
                    return (
                        <div className="global-navbar-preview__mobile-center">
                            <div className="global-navbar-preview__logo">
                                {pageSettings.livePageImg ? (
                                    <img 
                                        src={pageSettings.livePageImg} 
                                        alt="Logo"
                                        className="global-navbar-preview__logo-img"
                                        style={{
                                            maxWidth: `${effectiveSettings.logoMaxWidthMobile || 120}px`,
                                            width: `${effectiveSettings.logoMaxWidthMobile || 120}px`,
                                            height: 'auto'
                                        }}
                                    />
                                ) : (
                                    <span 
                                        className="global-navbar-preview__logo-text"
                                        style={{
                                            maxWidth: `${effectiveSettings.logoMaxWidthMobile || 120}px`,
                                            width: `${effectiveSettings.logoMaxWidthMobile || 120}px`
                                        }}
                                    >
                                        {pageSettings.name || 'Your Brand'}
                                    </span>
                                )}
                            </div>
                        </div>
                    );
            }
        }

        // DESKTOP LAYOUTS - Vollständige Navigation (Original Code)
        return (
            <>
                {effectiveSettings.showLogo && (
                    <div className="global-navbar-preview__logo">
                        {pageSettings.livePageImg ? (
                            <img 
                                src={pageSettings.livePageImg} 
                                alt="Logo"
                                className="global-navbar-preview__logo-img"
                                style={{
                                    maxWidth: `${effectiveSettings.logoMaxWidth || 150}px`
                                }}
                            />
                        ) : (
                            <span 
                                className="global-navbar-preview__logo-text"
                                style={{
                                    maxWidth: `${effectiveSettings.logoMaxWidth || 150}px`
                                }}
                            >
                                {pageSettings.name || 'Your Brand'}
                            </span>
                        )}
                    </div>
                )}
                
                {effectiveSettings.layoutType === 'traditional' && (
                    <div className={`global-navbar-preview__nav ${effectiveSettings.showLogo ? 'global-navbar-preview__nav--traditional' : 'global-navbar-preview__nav--no-logo'}`}>
                        {(effectiveSettings.navItems || []).slice(0, 4).map((item, index) => (
                            <span 
                                key={index} 
                                className={`global-navbar-preview__nav-item ${item.type === 'button' ? 'global-navbar-preview__nav-item--button' : ''}`}
                                style={{
                                    color: effectiveSettings.textColor || '#1f2937',
                                    ...(item.type === 'button' && {
                                        background: effectiveSettings.buttonBackgroundColor || '#667eea',
                                        color: effectiveSettings.buttonTextColor || '#ffffff',
                                        padding: '8px 16px',
                                        borderRadius: '8px',
                                        border: 'none'
                                    })
                                }}
                            >
                                {item.label || `Nav ${index + 1}`}
                            </span>
                        ))}
                        {(!effectiveSettings.navItems || effectiveSettings.navItems.length === 0) && (
                            <>
                                <span 
                                    className="global-navbar-preview__nav-item"
                                    style={{ color: effectiveSettings.textColor || '#1f2937' }}
                                >
                                    Home
                                </span>
                                <span 
                                    className="global-navbar-preview__nav-item"
                                    style={{ color: effectiveSettings.textColor || '#1f2937' }}
                                >
                                    About
                                </span>
                                <span 
                                    className="global-navbar-preview__nav-item"
                                    style={{ color: effectiveSettings.textColor || '#1f2937' }}
                                >
                                    Services
                                </span>
                                <span 
                                    className="global-navbar-preview__nav-item global-navbar-preview__nav-item--button"
                                    style={{
                                        background: effectiveSettings.buttonBackgroundColor || '#667eea',
                                        color: effectiveSettings.buttonTextColor || '#ffffff',
                                        padding: '8px 16px',
                                        borderRadius: `${effectiveSettings.buttonBorderRadius || 8}px`
                                    }}
                                >
                                    Contact
                                </span>
                            </>
                        )}
                    </div>
                )}
                
                {(effectiveSettings.layoutType === 'social-centric' || effectiveSettings.layoutType === 'hybrid') && (
                    <>
                        {effectiveSettings.layoutType === 'hybrid' && (
                            <div className="global-navbar-preview__nav">
                                {(effectiveSettings.navItems || []).slice(0, 2).map((item, index) => (
                                    <span key={index} className="global-navbar-preview__nav-item">
                                        {item.label || `Nav ${index + 1}`}
                                    </span>
                                ))}
                            </div>
                        )}
                        
                        <div className={`global-navbar-preview__social ${!effectiveSettings.ctaButton ? 'global-navbar-preview__social--no-cta' : ''}`}>
                            {(effectiveSettings.socialLinks || []).slice(0, 4).map((social, index) => (
                                <div 
                                    key={index} 
                                    className="global-navbar-preview__social-icon"
                                    style={{
                                        background: effectiveSettings.socialIconBackground || 'rgba(102, 126, 234, 0.1)',
                                        borderRadius: `${effectiveSettings.socialIconBorderRadius || 10}px`,
                                        width: `${effectiveSettings.socialIconSize || 40}px`,
                                        height: `${effectiveSettings.socialIconSize || 40}px`,
                                        padding: '8px',
                                        minWidth: '40px',
                                        minHeight: '40px'
                                    }}
                                >
                                    <img 
                                        src={`/img/${social.platform}.svg`} 
                                        alt={social.platform}
                                        style={{ 
                                            width: `${(effectiveSettings.socialIconSize || 40) * 0.6}px`, 
                                            height: `${(effectiveSettings.socialIconSize || 40) * 0.6}px`, 
                                            filter: effectiveSettings.socialIconInvert 
                                                ? 'brightness(0) invert(1)' 
                                                : effectiveSettings.textColor === '#f9fafb' || effectiveSettings.textColor === '#ffffff' 
                                                ? 'brightness(0) invert(1)' 
                                                : 'brightness(0)'
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                        
                        {effectiveSettings.ctaButton && (
                            <div 
                                className="global-navbar-preview__cta"
                                style={{
                                    background: effectiveSettings.ctaButton.style === 'outline' 
                                        ? 'transparent'
                                        : effectiveSettings.ctaButton.style === 'gradient'
                                        ? 'linear-gradient(135deg, #667eea, #764ba2)'
                                        : effectiveSettings.buttonBackgroundColor || '#667eea',
                                    border: effectiveSettings.ctaButton.style === 'outline' 
                                        ? `2px solid ${effectiveSettings.buttonBackgroundColor || effectiveSettings.textColor}` 
                                        : 'none',
                                    color: effectiveSettings.ctaButton.style === 'outline' 
                                        ? effectiveSettings.buttonBackgroundColor || effectiveSettings.textColor
                                        : effectiveSettings.buttonTextColor || 'white',
                                    borderRadius: `${effectiveSettings.buttonBorderRadius || 8}px`,
                                    padding: '10px 20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}
                            >
                                <span>{effectiveSettings.ctaButton.text || 'Get Started'}</span>
                                {effectiveSettings.ctaButton.iconPath && (
                                    <img 
                                        src={effectiveSettings.ctaButton.iconPath}
                                        alt="Button Icon"
                                        style={{
                                            width: '16px',
                                            height: '16px',
                                            filter: effectiveSettings.ctaButton.iconInvert 
                                                ? 'brightness(0) invert(1)' 
                                                : effectiveSettings.ctaButton.style === 'outline'
                                                ? 'brightness(0)'
                                                : 'brightness(0) invert(1)'
                                        }}
                                    />
                                )}
                            </div>
                        )}
                    </>
                )}
            </>
        );
    };
    
    // Access Control Integration
    const { products } = useUserProducts();

    const tabs = [
        { id: 'standard', label: 'Standard Settings', icon: 'settings' },
        { id: 'fonts', label: 'Typography', icon: 'typography' },
        { id: 'access', label: 'Access Control', icon: 'access' },
        { id: 'seo', label: 'SEO Settings', icon: 'seo' }
    ];

    const renderTabIcon = (iconType) => {
        switch (iconType) {
            case 'settings':
                return (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5842 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6642 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6642 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.01127 9.77251C4.28054 9.5799 4.48571 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                );
            case 'typography':
                return (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 7V4H20V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9 20H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 4V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                );
            case 'access':
                return (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 8h-1V6a5 5 0 1 0-10 0v2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zM9 6a3 3 0 1 1 6 0v2H9V6z" fill="currentColor"/>
                        <circle cx="12" cy="15" r="2" fill="white"/>
                        <path d="M12 17v2" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                );
            case 'seo':
                return (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                        <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2"/>
                        <path d="M11 6V14" stroke="currentColor" strokeWidth="2"/>
                        <path d="M8 9L14 9" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                );
            default:
                return null;
        }
    };

    // Handler functions for child components
    const handleSettingsChange = (name, value) => {
        setPageSettings(prevSettings => ({
            ...prevSettings,
            [name]: value
        }));
    };

    const checkDomainUniqueness = async (domainName) => {
        if (!domainName) return true; // Keine Überprüfung, wenn kein Name eingegeben wurde
        const publicPageRef = dbRef(db, `/publicPages/${domainName}`);
        const snapshot = await get(publicPageRef);
        return !snapshot.exists(); // True, wenn der Name eindeutig ist
    };

    const handleBigFontChange = (font) => {
        setSelectedFont(font);
        setPageSettings(prevSettings => ({
            ...prevSettings,
            globalBigFont: font
        }));
    };

    const handleSmallFontChange = (font) => {
        setSelectedSmallFont(font);
        setPageSettings(prevSettings => ({
            ...prevSettings,
            globalSmallFont: font
        }));
    };

    const handleColorChange = (colorKey, newValue) => {
        const newColors = { ...pageSettings.colors, [colorKey]: newValue };
        setPageSettings(prevSettings => ({ ...prevSettings, colors: newColors }));
    };

    const handleBackgroundColorChange = (color) => {
        setPageSettings(prevSettings => ({ ...prevSettings, backgroundColor: color }));
    };

    // Access Control Handler
    const handleUpdatePageData = (updatedPageData) => {
        console.log('🔒 GlobalSettingsModal: Updating page data with access control:', updatedPageData);
        setPageSettings(prevSettings => ({
            ...prevSettings,
            ...updatedPageData
        }));
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            onClose();
        }, 400); // Match animation duration
    };

    useEffect(() => {
        if (show && currentUser && pageId) {
            loadPageSettings();
        }
    }, [show, currentUser, pageId, db, defaultBigFont, defaultSmallFont]);
    
    /**
     * 🎨 Update Hero Header Fonts in contentElements
     * Replaces fonts in all Hero Header widgets on a page
     */
    const updateHeroHeaderFonts = async (targetPageId, newBigFont, newSmallFont) => {
        try {
            console.log('🔧 updateHeroHeaderFonts called:', { targetPageId, newBigFont, newSmallFont });
            
            const pageRef = dbRef(db, `/pages/${targetPageId}`);
            const pageSnapshot = await get(pageRef);
            
            if (!pageSnapshot.exists()) {
                console.warn('⚠️ Page not found:', targetPageId);
                return false;
            }
            
            const pageData = pageSnapshot.val();
            const widgets = pageData.widgets || [];
            
            console.log(`📊 Found ${widgets.length} widgets on page`);
            
            let hasChanges = false;
            const updatedWidgets = widgets.map((widget, widgetIndex) => {
                // Only update Hero Header widgets
                if (widget.type !== 'HeroHeader') return widget;
                
                console.log(`🎯 Found Hero Header widget #${widgetIndex}:`, {
                    hasContentElements: !!widget.data?.contentElements,
                    contentElementsLength: widget.data?.contentElements?.length || 0,
                    isArray: Array.isArray(widget.data?.contentElements),
                    hasContainerSections: !!widget.data?.containerSections,
                    containerSectionsLength: widget.data?.containerSections?.length || 0,
                    widgetDataKeys: Object.keys(widget.data || {}).slice(0, 15)
                });
                
                // ✅ CRITICAL FIX: Migriere Widget zu containerSections, falls nötig!
                let workingWidgetData = widget.data;
                if (!widget.data?.containerSections || widget.data.containerSections.length === 0) {
                    console.log(`🔄 Widget #${widgetIndex} needs migration - running migrateToContainerSections...`);
                    workingWidgetData = migrateToContainerSections(widget.data);
                    console.log(`✅ Migration complete:`, {
                        hasContainerSections: !!workingWidgetData.containerSections,
                        containerSectionsCount: workingWidgetData.containerSections?.length || 0
                    });
                }
                
                // Check if widget uses containerSections with contentElements inside
                if (workingWidgetData?.containerSections && Array.isArray(workingWidgetData.containerSections)) {
                    console.log(`📦 Widget #${widgetIndex} uses containerSections (new format)`);
                    
                    const updatedContainers = workingWidgetData.containerSections.map((container, containerIndex) => {
                        if (!container.contentElements || !Array.isArray(container.contentElements)) {
                            console.log(`  ⏭️  Container ${containerIndex}: No contentElements, skipping`);
                            return container;
                        }
                        
                        console.log(`  📦 Container ${containerIndex} has ${container.contentElements.length} elements`);
                        console.log(`  📦 Container ${containerIndex} contentElements:`, container.contentElements.map(el => ({
                            type: el.type,
                            elementType: el.elementType,
                            fontFamily: el.fontFamily || '(empty)'
                        })));
                        
                        const updatedElements = container.contentElements.map((element, elIndex) => {
                            if (element.type !== 'text') {
                                console.log(`    ⏭️  Element ${elIndex}: Skipping (type=${element.type})`);
                                return element;
                            }
                            
                            const newFontFamily = element.elementType === 'h1' 
                                ? newBigFont 
                                : newSmallFont;
                            
                            const currentFont = element.fontFamily || '';
                            
                            console.log(`    📝 Element ${elIndex} (${element.elementType}):`, {
                                currentFont: currentFont || '(empty)',
                                newFont: newFontFamily,
                                willChange: currentFont !== newFontFamily
                            });
                            
                            if (currentFont !== newFontFamily) {
                                hasChanges = true;
                            }
                            
                            return {
                                ...element,
                                fontFamily: newFontFamily
                            };
                        });
                        
                        return {
                            ...container,
                            contentElements: updatedElements
                        };
                    });
                    
                    return {
                        ...widget,
                        data: {
                            ...workingWidgetData,
                            containerSections: updatedContainers
                        }
                    };
                }
                
                // Fallback: Update contentElements directly (old format)
                if (workingWidgetData?.contentElements && Array.isArray(workingWidgetData.contentElements)) {
                    console.log(`📝 Widget #${widgetIndex} contentElements:`, workingWidgetData.contentElements.map(el => ({
                        type: el.type,
                        elementType: el.elementType,
                        currentFont: el.fontFamily || '(empty)',
                        content: el.content?.substring(0, 30) || ''
                    })));
                    
                    const updatedElements = workingWidgetData.contentElements.map((element, elIndex) => {
                        // Skip non-text elements
                        if (element.type !== 'text') {
                            console.log(`  ⏭️  Element ${elIndex}: Skipping (type=${element.type})`);
                            return element;
                        }
                        
                        // Map element types to fonts
                        // h1 = Big Font, h2/h3/p = Small Font
                        const newFontFamily = element.elementType === 'h1' 
                            ? newBigFont 
                            : newSmallFont;
                        
                        // ✅ ALWAYS update fontFamily (auch wenn leer oder undefined)
                        // Das ist wichtig, weil leere Fonts den Fallback verwenden
                        const currentFont = element.fontFamily || '';
                        
                        console.log(`  📝 Element ${elIndex} (${element.elementType}):`, {
                            currentFont: currentFont || '(empty)',
                            newFont: newFontFamily,
                            willChange: currentFont !== newFontFamily
                        });
                        
                        if (currentFont !== newFontFamily) {
                            hasChanges = true;
                        }
                        
                        return {
                            ...element,
                            fontFamily: newFontFamily
                        };
                    });
                    
                    return {
                        ...widget,
                        data: {
                            ...workingWidgetData,
                            contentElements: updatedElements
                        }
                    };
                }
                
                // If no contentElements or containerSections, return with migrated data
                return {
                    ...widget,
                    data: workingWidgetData
                };
            });
            
            // ✅ CRITICAL FIX: Immer speichern, auch wenn hasChanges=false
            // Das liegt daran, dass leere fontFamily-Werte jetzt explizit gesetzt werden
            console.log(`💾 Saving updated widgets (hasChanges: ${hasChanges})`);
            
            await set(pageRef, {
                ...pageData,
                widgets: updatedWidgets
            });
            console.log('✅ Hero Header fonts updated for page:', targetPageId);
            return true;
        } catch (error) {
            console.error('❌ Error updating Hero Header fonts:', error);
            return false;
        }
    };
    
    /**
     * 🚀 Update Hero Header Fonts for entire Space
     * Updates all pages in the space
     */
    const updateSpaceHeroHeaderFonts = async (userId, spaceId, newBigFont, newSmallFont) => {
        try {
            // Get all pages in the space
            const pagesRef = dbRef(db, `/pages`);
            const pagesSnapshot = await get(pagesRef);
            
            if (!pagesSnapshot.exists()) {
                console.warn('⚠️ No pages found');
                return 0;
            }
            
            const allPages = pagesSnapshot.val();
            let updatedCount = 0;
            
            // Filter pages that belong to this space
            const spacePageIds = Object.keys(allPages).filter(pageIdKey => {
                const parts = pageIdKey.split('/');
                return parts.length === 3 && parts[0] === userId && parts[1] === spaceId;
            });
            
            console.log(`🔄 Updating ${spacePageIds.length} pages in space...`);
            
            // Update each page
            for (const spacePageId of spacePageIds) {
                const wasUpdated = await updateHeroHeaderFonts(spacePageId, newBigFont, newSmallFont);
                if (wasUpdated) updatedCount++;
            }
            
            console.log(`✅ Updated ${updatedCount} pages in space`);
            return updatedCount;
        } catch (error) {
            console.error('❌ Error updating space Hero Header fonts:', error);
            return 0;
        }
    };
    
    /**
     * 🎨 Handle Font Sync Modal Confirmation - FIXED VERSION
     */
    const handleFontSyncConfirm = async (syncOptions) => {
        console.log('🎨 [FONT-SYNC] handleFontSyncConfirm called:', syncOptions);
        setShowFontSyncModal(false);
        
        if (!pendingSaveData) {
            console.error('❌ [FONT-SYNC] No pending save data!');
            return;
        }
        
        let { updatedSettings } = pendingSaveData;
        const newBigFont = selectedFont || defaultBigFont;
        const newSmallFont = selectedSmallFont || defaultSmallFont;
        
        // Apply font sync if requested
        if (syncOptions.shouldSync) {
            console.log('🔄 [FONT-SYNC] Font sync requested:', { newBigFont, newSmallFont, level: syncOptions.level });
            
            try {
                // ✅ WICHTIG: Replace Fonts IN updatedSettings, nicht separat speichern!
                console.log('🔤 [FONT-SYNC] Replacing fonts in widgets...');
                
                // Replace fonts in updatedSettings.widgets (falls vorhanden)
                if (updatedSettings.widgets && Array.isArray(updatedSettings.widgets)) {
                    const updatedWidgets = updatedSettings.widgets.map((widget, index) => {
                        if (widget.type !== 'HeroHeader') {
                            return widget;
                        }
                        
                        console.log(`🎯 [FONT-SYNC] Processing HeroHeader #${index}`);
                        
                        // Replace fonts using utility
                        return replaceHeroHeaderFonts(widget, newBigFont, newSmallFont);
                    });
                    
                    // Update updatedSettings mit neuen Widgets
                    updatedSettings = {
                        ...updatedSettings,
                        widgets: updatedWidgets
                    };
                    
                    console.log('✅ [FONT-SYNC] Fonts replaced in widgets!');
                } else {
                    console.warn('⚠️ [FONT-SYNC] No widgets found in updatedSettings');
                }
            } catch (error) {
                console.error('❌ [FONT-SYNC] Error replacing fonts:', error);
            }
        } else {
            console.log('⏭️  [FONT-SYNC] Font sync skipped by user');
        }
        
        // Complete the save (with reload flag if font sync was performed)
        await completeSave(updatedSettings, syncOptions.shouldSync);
    };
    
    /**
     * 💾 Complete the save operation
     */
    const completeSave = async (updatedSettings, shouldReload = false) => {
        try {
            const pageRef = dbRef(db, `/pages/${pageId}`);
            await set(pageRef, updatedSettings);
            
            // If Space Page, save to space
            if (pageInfo.isSpacePage) {
                const spaceRef = dbRef(db, `/users/${pageInfo.userId}/spaces/${pageInfo.spaceId}`);
                const spaceUpdates = {
                    globalBigFont: selectedFont || defaultBigFont,
                    globalSmallFont: selectedSmallFont || defaultSmallFont,
                    colors: updatedSettings.colors,
                    backgroundColor: updatedSettings.backgroundColor,
                    // ✅ WICHTIG: Auch in designSettings speichern für Kompatibilität
                    'designSettings/globalBigFont': selectedFont || defaultBigFont,
                    'designSettings/globalSmallFont': selectedSmallFont || defaultSmallFont
                };
                await update(spaceRef, spaceUpdates);  // ✅ UPDATE statt SET - behält andere Daten!
                console.log('✅ Space Typography Settings updated');
            }
            
            console.log('✅ Settings saved successfully');
            setPreviousCustomDomain(updatedSettings.customDomain);
            setPendingSaveData(null);
            handleClose();
            
            // ✅ Reload page if font sync was performed
            if (shouldReload) {
                console.log('🔄 Reloading page to apply font changes...');
                setTimeout(() => {
                    window.location.reload();
                }, 300);
            }
        } catch (error) {
            console.error('❌ Error saving settings:', error);
            alert('Failed to save settings. Please try again.');
        }
    };

    const loadPageSettings = async () => {
        try {
            const pageRef = dbRef(db, `/pages/${pageId}`);
            const pageSnapshot = await get(pageRef);
            
            if (pageSnapshot.exists()) {
                const data = pageSnapshot.val();
                
                // Store old fonts for comparison
                setOldFonts({
                    globalBigFont: data.globalBigFont || defaultBigFont,
                    globalSmallFont: data.globalSmallFont || defaultSmallFont
                });
                
                // If this is a space-based page, try to load space settings first
                let spaceSettings = {};
                if (pageInfo.isSpacePage) {
                    try {
                        const spaceRef = dbRef(db, `/users/${pageInfo.userId}/spaces/${pageInfo.spaceId}`);
                        const spaceSnapshot = await get(spaceRef);
                        if (spaceSnapshot.exists()) {
                            spaceSettings = spaceSnapshot.val();
                            console.log('📊 Loaded space settings for page:', spaceSettings);
                        }
                    } catch (error) {
                        console.warn('⚠️ Could not load space settings:', error);
                    }
                }
                
                // Merge page data with space settings (page settings override space settings)
                const mergedData = {
                    ...data,
                    // Apply space settings first (if available)
                    globalBigFont: spaceSettings.globalBigFont || data.globalBigFont || defaultBigFont,
                    globalSmallFont: spaceSettings.globalSmallFont || data.globalSmallFont || defaultSmallFont,
                    colors: spaceSettings.colors || data.colors || {
                        color1: '#FFFFFF',
                        color2: '#000000',
                        color3: '#FF0000',
                        color4: '#00FF00',
                    },
                    backgroundColor: spaceSettings.backgroundColor || data.backgroundColor || '#FFFFFF',
                    navBarSettings: {
                        // Default settings
                        enabled: false,
                        position: 'top',
                        backgroundStyle: 'solid',
                        backgroundColor: '#ffffff',
                        glassmorphism: false,
                        floatingGlass: false,
                        textColor: '#1f2937',
                        buttonBackgroundColor: '#667eea',
                        buttonTextColor: '#ffffff',
                        height: 70,
                        width: 100,
                        borderRadius: 0,
                        opacity: 100,
                        backdropBlur: 0,
                        showLogo: true,
                        layoutType: 'traditional',
                        logoMaxWidth: 150,
                        logoMaxWidthMobile: 120,
                        socialIconBackground: 'rgba(102, 126, 234, 0.1)',
                        socialIconInvert: false,
                        socialIconBorderRadius: 10,
                        socialIconSize: 40,
                        buttonBorderRadius: 8,
                        buttonWidth: 'auto',
                        mobileCollapse: true,
                        smoothScroll: true,
                        hideOnScroll: false,
                        navItems: [],
                        socialLinks: [],
                        ctaButton: null,
                        // Override with saved settings if they exist
                        ...(data.navBarSettings || {})
                    }
                };
                
                setPageSettings(mergedData);
                setPreviousCustomDomain(data.customDomain);
                setSelectedFont(mergedData.globalBigFont);
                setSelectedSmallFont(mergedData.globalSmallFont);
            }
        } catch (error) {
            console.error('❌ Error loading page settings:', error);
        }
    };


    const saveSettings = async () => {
        if (currentUser && pageId) {
            // Wenn der Domainname unverändert bleibt, überspringe die Eindeutigkeitsprüfung
            if (pageSettings.customDomain !== previousCustomDomain) {
                const isUnique = await checkDomainUniqueness(pageSettings.customDomain);
                if (!isUnique) {
                    alert('Dieser Domainname ist bereits vergeben. Bitte wählen Sie einen anderen.');
                    return;
                }
            }

            const updatedSettings = {
                ...pageSettings,
                globalBigFont: selectedFont || defaultBigFont,
                globalSmallFont: selectedSmallFont || defaultSmallFont,
            };

            // Entferne den alten Domainnamen aus /publicPages, falls er geändert wurde und nicht leer ist
            if (previousCustomDomain && pageSettings.customDomain !== previousCustomDomain) {
                const oldPublicPageRef = dbRef(db, `/publicPages/${previousCustomDomain}`);
                set(oldPublicPageRef, null);
            }

            // Speichere den neuen Domainnamen in /publicPages, wenn er geändert wurde und nicht leer ist
            if (pageSettings.customDomain && pageSettings.customDomain !== previousCustomDomain) {
                const publicPageRef = dbRef(db, `/publicPages/${pageSettings.customDomain}`);
                await set(publicPageRef, { pageId: pageId, userId: currentUser.uid });
            }

            // ✨ Check if fonts have changed - Show Font Sync Modal
            const newBigFont = selectedFont || defaultBigFont;
            const newSmallFont = selectedSmallFont || defaultSmallFont;
            
            console.log('🔍 Font Change Detection:', {
                oldBigFont: oldFonts?.globalBigFont,
                newBigFont,
                oldSmallFont: oldFonts?.globalSmallFont,
                newSmallFont,
                hasOldFonts: !!oldFonts
            });
            
            const fontsChanged = oldFonts && (
                oldFonts.globalBigFont !== newBigFont ||
                oldFonts.globalSmallFont !== newSmallFont
            );

            if (fontsChanged) {
                console.log('🎨 Fonts changed - showing Font Sync Modal');
                // Store pending save data
                setPendingSaveData({ updatedSettings });
                // Show Font Sync Modal
                setShowFontSyncModal(true);
            } else {
                // No font changes, save directly
                console.log('💾 No font changes detected - saving directly without modal');
                await completeSave(updatedSettings);
            }
        }
    };

    const renderActiveTab = () => {
        switch (activeTab) {
            case 'standard':
                return (
                    <StandardSettings 
                        pageSettings={pageSettings}
                        onSettingsChange={handleSettingsChange}
                    />
                );
            case 'fonts':
                return (
                    <FontsSettings 
                        selectedBigFont={selectedFont}
                        selectedSmallFont={selectedSmallFont}
                        onBigFontChange={handleBigFontChange}
                        onSmallFontChange={handleSmallFontChange}
                        pageSettings={pageSettings}
                        onColorChange={handleColorChange}
                        onBackgroundColorChange={handleBackgroundColorChange}
                        isReadOnly={false}
                        isSpacePage={pageInfo.isSpacePage}
                    />
                );
            case 'access':
                return (
                    <AccessControl 
                        pageData={pageSettings}
                        onUpdatePageData={handleUpdatePageData}
                        products={products}
                        loading={false}
                    />
                );
            case 'seo':
                return (
                    <SEOSettings 
                        pageId={pageId}
                        pageData={pageSettings}
                        onUpdate={(updatedPageData) => {
                            setPageSettings(updatedPageData);
                        }}
                        isGlobalModal={true}
                        onSEOChange={(seoData) => {
                            setPageSettings(prevSettings => ({
                                ...prevSettings,
                                seoSettings: seoData
                            }));
                        }}
                    />
                );
            default:
                return null;
        }
    };

    if (!show) return null;

    return (
        <>
            <div className="modal-background-big">
                <div className={`modal-content-big ${isClosing ? 'closing' : ''}`}>
                    <div className="modal-header">
                        <h2 className="modal-title">Page Settings: {pageSettings.name}</h2>
                        <div className="modal-buttons">
                            <button className="modal-button" onClick={handleClose}>
                                Close
                            </button>
                            <button className="modal-button primary" onClick={saveSettings}>
                                Save Changes
                            </button>
                        </div>
                    </div>

                    <div className="page-settings-tabs-container">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                className={`page-settings-tab-button ${activeTab === tab.id ? 'page-settings-tab-button--active' : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                <span className="page-settings-tab-icon">{renderTabIcon(tab.icon)}</span>
                                <span className="page-settings-tab-label">{tab.label}</span>
                            </button>
                        ))}
                    </div>

                    <div className="modal-content-inner">
                        <div className="tab-content">
                            {renderActiveTab()}
                        </div>
                    </div>
                </div>
            </div>

            {/* Font Sync Modal */}
            <FontSyncModal
                show={showFontSyncModal}
                onClose={() => setShowFontSyncModal(false)}
                onConfirm={handleFontSyncConfirm}
                oldFonts={oldFonts}
                newFonts={{
                    globalBigFont: selectedFont || defaultBigFont,
                    globalSmallFont: selectedSmallFont || defaultSmallFont
                }}
                isSpacePage={pageInfo.isSpacePage}
            />
        </>
    )
}

export default GlobalSettingsModal;
